import { useState, useEffect, useRef } from 'react';
import { theme } from '../../theme';
import { useIsMobile } from '../../hooks/useMediaQuery';

const DOC_TYPES = [
  { key: 'factura', label: 'Factura de venta', tplField: 'factura_template' },
  { key: 'pos', label: 'Factura POS', tplField: 'pos_template' },
  { key: 'cotizacion', label: 'Cotización', tplField: 'cotizacion_template' },
  { key: 'nota_credito', label: 'Nota crédito', tplField: 'nota_credito_template' },
  { key: 'nota_debito', label: 'Nota débito', tplField: 'nota_debito_template' },
];

const TABS = [
  { id: 'legal', label: 'Datos legales' },
  { id: 'dian', label: 'Facturación electrónica (DIAN)' },
  { id: 'plantillas', label: 'Plantillas y logo' },
  { id: 'prefijos', label: 'Prefijos y consecutivos' },
];

const c = theme.colors;

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: '7px',
  border: `1px solid ${c.border}`,
  background: c.background,
  color: c.text,
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '0.8rem', color: c.textMuted, marginBottom: '5px' };
const fieldWrap = { marginBottom: '14px' };
const card = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px' };

function Field({ label, children }) {
  return (
    <div style={fieldWrap}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function CajeroConfig({ token }) {
  const isMobile = useIsMobile();
  const cols2 = isMobile ? '1fr' : '1fr 1fr';
  const colsPrefix = isMobile ? '1fr' : '1.3fr 1fr 1fr';
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('legal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const certInput = useRef(null);
  const [certPassword, setCertPassword] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch('/api/cajero/config', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setCfg(data); })
      .finally(() => setLoading(false));
  }, [token]);

  const set = (field, value) => setCfg((prev) => ({ ...prev, [field]: value }));
  const setResolution = (typeKey, field, value) =>
    setCfg((prev) => ({
      ...prev,
      dian_resolutions: { ...(prev.dian_resolutions || {}), [typeKey]: { ...((prev.dian_resolutions || {})[typeKey] || {}), [field]: value } },
    }));
  const setPrefix = (typeKey, field, value) =>
    setCfg((prev) => ({
      ...prev,
      prefixes: { ...prev.prefixes, [typeKey]: { ...(prev.prefixes?.[typeKey] || {}), [field]: value } },
    }));

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3500);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        razon_social: cfg.razon_social, nit: cfg.nit, dv: cfg.dv, tipo_persona: cfg.tipo_persona,
        regimen: cfg.regimen, direccion: cfg.direccion, ciudad: cfg.ciudad, departamento: cfg.departamento,
        telefono: cfg.telefono, email: cfg.email,
        show_logo: cfg.show_logo, logo_url: cfg.logo_url,
        factura_template: cfg.factura_template, pos_template: cfg.pos_template,
        cotizacion_template: cfg.cotizacion_template, nota_credito_template: cfg.nota_credito_template,
        nota_debito_template: cfg.nota_debito_template,
        prefixes: cfg.prefixes,
        dian_enabled: cfg.dian_enabled, dian_env: cfg.dian_env,
        dian_software_id: cfg.dian_software_id, dian_test_set_id: cfg.dian_test_set_id,
        dian_resolutions: cfg.dian_resolutions,
      };
      if (cfg._dian_cert_password) body.dian_cert_password = cfg._dian_cert_password;
      if (cfg._dian_software_pin) body.dian_software_pin = cfg._dian_software_pin;

      const r = await fetch('/api/cajero/config', { method: 'PATCH', headers, body: JSON.stringify(body) });
      const data = await r.json();
      if (r.ok) { setCfg({ ...data, _dian_cert_password: '', _dian_software_pin: '' }); flash('Configuración guardada'); }
      else flash(data.message || 'No se pudo guardar', false);
    } catch {
      flash('Error de conexión', false);
    } finally {
      setSaving(false);
    }
  };

  const uploadCert = async () => {
    const file = certInput.current?.files?.[0];
    if (!file) return flash('Selecciona un archivo .p12', false);
    const fd = new FormData();
    fd.append('cert', file);
    if (certPassword) fd.append('dian_cert_password', certPassword);
    try {
      const r = await fetch('/api/cajero/config/cert', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok) { setCfg((prev) => ({ ...prev, has_cert: true })); setCertPassword(''); if (certInput.current) certInput.current.value = ''; flash('Certificado cargado'); }
      else flash(data.message || 'No se pudo cargar', false);
    } catch {
      flash('Error de conexión', false);
    }
  };

  if (loading) return <div style={{ padding: '2rem', color: c.textMuted }}>Cargando…</div>;
  if (!cfg) return <div style={{ padding: '2rem', color: c.error }}>No se pudo cargar la configuración.</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <h1 style={{ color: c.primary, fontSize: '1.5rem', marginBottom: '4px' }}>Configuración del Cajero</h1>
      <p style={{ color: c.textMuted, fontSize: '0.9rem', marginTop: 0 }}>
        Datos con los que se emiten y se imprimen tus facturas, POS, cotizaciones y notas.
      </p>

      {/* Pestañas */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '18px 0' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem',
            border: `1px solid ${tab === t.id ? c.primary : c.border}`,
            background: tab === t.id ? `${c.primary}22` : 'transparent',
            color: tab === t.id ? c.primary : c.text, fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Datos legales ── */}
      {tab === 'legal' && (
        <div style={card}>
          <p style={{ color: c.textMuted, fontSize: '0.85rem', marginTop: 0 }}>
            Empresa a nombre de la cual se emiten los documentos. Ningún campo es obligatorio: llena lo que necesites.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: '0 16px' }}>
            <Field label="Razón social / Nombre"><input style={inputStyle} value={cfg.razon_social || ''} onChange={(e) => set('razon_social', e.target.value)} /></Field>
            <Field label="Tipo de persona">
              <select style={inputStyle} value={cfg.tipo_persona || ''} onChange={(e) => set('tipo_persona', e.target.value)}>
                <option value="">—</option>
                <option value="juridica">Jurídica</option>
                <option value="natural">Natural</option>
              </select>
            </Field>
            <Field label="NIT / Cédula"><input style={inputStyle} value={cfg.nit || ''} onChange={(e) => set('nit', e.target.value)} /></Field>
            <Field label="Dígito de verificación (DV)"><input style={inputStyle} value={cfg.dv || ''} onChange={(e) => set('dv', e.target.value)} /></Field>
            <Field label="Régimen"><input style={inputStyle} value={cfg.regimen || ''} onChange={(e) => set('regimen', e.target.value)} placeholder="Ej: Responsable de IVA" /></Field>
            <Field label="Teléfono"><input style={inputStyle} value={cfg.telefono || ''} onChange={(e) => set('telefono', e.target.value)} /></Field>
            <Field label="Correo"><input style={inputStyle} value={cfg.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field label="Ciudad"><input style={inputStyle} value={cfg.ciudad || ''} onChange={(e) => set('ciudad', e.target.value)} /></Field>
            <Field label="Departamento"><input style={inputStyle} value={cfg.departamento || ''} onChange={(e) => set('departamento', e.target.value)} /></Field>
          </div>
          <Field label="Dirección"><input style={inputStyle} value={cfg.direccion || ''} onChange={(e) => set('direccion', e.target.value)} /></Field>
        </div>
      )}

      {/* ── DIAN ── */}
      {tab === 'dian' && (
        <div style={card}>
          {/* Letrero informativo amable */}
          <div style={{
            background: `${c.primary}11`, border: `1px solid ${c.primary}55`, borderRadius: '10px',
            padding: '14px 16px', marginBottom: '18px', fontSize: '0.86rem', lineHeight: 1.5, color: c.text,
          }}>
            <strong style={{ color: c.primary }}>Facturación electrónica DIAN (opcional).</strong>{' '}
            Si activas esta opción, cada factura que emitas se enviará <strong>directamente a la DIAN</strong> y
            quedará radicada oficialmente — para esto tu empresa ya debe estar <strong>en etapa de
            producción / habilitada</strong> ante la DIAN (certificado, software y resolución de numeración).
            <br /><br />
            Si <strong>no</strong> la activas, no pasa nada: puedes seguir facturando con total normalidad
            <strong> solo en tu sistema interno</strong>, sin que la información llegue a la DIAN. Actívala
            únicamente cuando estés listo para producción.
            <br /><br />
            <strong style={{ color: c.primary }}>Solo aplica para empresas de Colombia.</strong> La DIAN es
            la entidad tributaria colombiana. Si tu empresa es de <strong>otro país</strong>, no llenes este
            formulario: el cajero (facturas, POS, cotizaciones y notas) funciona igual de bien en tu sistema
            interno sin la DIAN.
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '16px' }}>
            <input type="checkbox" checked={!!cfg.dian_enabled} onChange={(e) => set('dian_enabled', e.target.checked)} />
            <span style={{ fontSize: '0.95rem' }}>Enviar mis documentos a la DIAN (facturación electrónica activa)</span>
          </label>

          {cfg.dian_enabled && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: '0 16px' }}>
                <Field label="Ambiente">
                  <select style={inputStyle} value={cfg.dian_env || 'test'} onChange={(e) => set('dian_env', e.target.value)}>
                    <option value="test">Habilitación (pruebas)</option>
                    <option value="prod">Producción</option>
                  </select>
                </Field>
                <Field label="Software ID"><input style={inputStyle} value={cfg.dian_software_id || ''} onChange={(e) => set('dian_software_id', e.target.value)} /></Field>
                <Field label="Software PIN">
                  <input style={inputStyle} type="password" placeholder={cfg.has_software_pin ? '•••••• (guardado)' : ''}
                    value={cfg._dian_software_pin || ''} onChange={(e) => set('_dian_software_pin', e.target.value)} />
                </Field>
                <Field label="Test Set ID (set de pruebas)"><input style={inputStyle} value={cfg.dian_test_set_id || ''} onChange={(e) => set('dian_test_set_id', e.target.value)} /></Field>
              </div>

              <div style={{ borderTop: `1px solid ${c.border}`, margin: '10px 0 16px' }} />
              <p style={{ color: c.text, fontSize: '0.9rem', fontWeight: 600, margin: '0 0 10px' }}>
                Certificado digital (.p12) {cfg.has_cert && <span style={{ color: c.success, fontSize: '0.8rem' }}>✓ cargado</span>}
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <label style={labelStyle}>Archivo .p12</label>
                  <input ref={certInput} type="file" accept=".p12,.pfx" style={{ ...inputStyle, padding: '7px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={labelStyle}>Contraseña del certificado</label>
                  <input style={inputStyle} type="password" value={certPassword} onChange={(e) => setCertPassword(e.target.value)} placeholder={cfg.has_cert_password ? '•••••• (guardada)' : ''} />
                </div>
                <button onClick={uploadCert} style={{
                  padding: '9px 16px', borderRadius: '7px', border: `1px solid ${c.primary}`,
                  background: c.primary, color: c.background, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
                }}>Cargar certificado</button>
              </div>
              <p style={{ color: c.textMuted, fontSize: '0.78rem', marginTop: '10px' }}>
                El certificado se guarda de forma privada en el servidor; nunca se expone públicamente.
              </p>

              <div style={{ borderTop: `1px solid ${c.border}`, margin: '18px 0 16px' }} />
              <p style={{ color: c.text, fontSize: '0.9rem', fontWeight: 600, margin: '0 0 4px' }}>Resolución de numeración</p>
              <p style={{ color: c.textMuted, fontSize: '0.78rem', margin: '0 0 12px' }}>
                Datos que te entrega la DIAN al habilitar cada tipo de documento (la clave técnica se usa para el CUFE de las facturas).
              </p>
              {[{ key: 'factura', label: 'Factura' }, { key: 'pos', label: 'POS' }].map((t) => {
                const r = cfg.dian_resolutions?.[t.key] || {};
                return (
                  <div key={t.key} style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '0.82rem', color: c.primary, marginBottom: '6px' }}>{t.label}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: '0 14px' }}>
                      <Field label="No. de resolución"><input style={inputStyle} value={r.numero || ''} onChange={(e) => setResolution(t.key, 'numero', e.target.value)} /></Field>
                      <Field label="Clave técnica"><input style={inputStyle} value={r.clave_tecnica || ''} onChange={(e) => setResolution(t.key, 'clave_tecnica', e.target.value)} /></Field>
                      <Field label="Rango desde"><input style={inputStyle} value={r.rango_desde || ''} onChange={(e) => setResolution(t.key, 'rango_desde', e.target.value)} /></Field>
                      <Field label="Rango hasta"><input style={inputStyle} value={r.rango_hasta || ''} onChange={(e) => setResolution(t.key, 'rango_hasta', e.target.value)} /></Field>
                      <Field label="Vigencia desde"><input type="date" style={inputStyle} value={r.vigencia_desde || ''} onChange={(e) => setResolution(t.key, 'vigencia_desde', e.target.value)} /></Field>
                      <Field label="Vigencia hasta"><input type="date" style={inputStyle} value={r.vigencia_hasta || ''} onChange={(e) => setResolution(t.key, 'vigencia_hasta', e.target.value)} /></Field>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── Plantillas y logo ── */}
      {tab === 'plantillas' && (
        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
            <input type="checkbox" checked={!!cfg.show_logo} onChange={(e) => set('show_logo', e.target.checked)} />
            <span style={{ fontSize: '0.92rem' }}>Mostrar el logo de la empresa en los documentos impresos</span>
          </label>
          {cfg.show_logo && (
            <Field label="URL del logo"><input style={inputStyle} value={cfg.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} placeholder="https://…/logo.png" /></Field>
          )}
          <div style={{ borderTop: `1px solid ${c.border}`, margin: '12px 0 16px' }} />
          <p style={{ color: c.textMuted, fontSize: '0.85rem', marginTop: 0 }}>
            Elige el diseño con el que se imprimirá cada tipo de documento.
          </p>
          {DOC_TYPES.map((d) => (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${c.border}55` }}>
              <span style={{ fontSize: '0.9rem' }}>{d.label}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[1, 2].map((n) => (
                  <button key={n} onClick={() => set(d.tplField, n)} style={{
                    padding: '6px 14px', borderRadius: '7px', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'inherit',
                    border: `1px solid ${Number(cfg[d.tplField]) === n ? c.primary : c.border}`,
                    background: Number(cfg[d.tplField]) === n ? `${c.primary}22` : 'transparent',
                    color: Number(cfg[d.tplField]) === n ? c.primary : c.text,
                  }}>{n === 1 ? 'Clásica' : 'Moderna'}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Prefijos y consecutivos ── */}
      {tab === 'prefijos' && (
        <div style={card}>
          <p style={{ color: c.textMuted, fontSize: '0.85rem', marginTop: 0 }}>
            Define el prefijo y el próximo número (consecutivo) con el que iniciará cada tipo de documento.
          </p>
          {DOC_TYPES.map((d) => {
            const p = cfg.prefixes?.[d.key] || { prefix: '', next: 1 };
            return (
              <div key={d.key} style={{ display: 'grid', gridTemplateColumns: colsPrefix, gap: '10px', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${c.border}55` }}>
                <span style={{ fontSize: '0.9rem' }}>{d.label}</span>
                <input style={inputStyle} placeholder="Prefijo (ej: FE)" value={p.prefix || ''} onChange={(e) => setPrefix(d.key, 'prefix', e.target.value)} />
                <input style={inputStyle} type="number" onWheel={(e) => e.currentTarget.blur()} min="1" placeholder="Próximo #" value={p.next ?? 1} onChange={(e) => setPrefix(d.key, 'next', parseInt(e.target.value) || 1)} />
              </div>
            );
          })}
        </div>
      )}

      {/* Guardar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
        <button onClick={save} disabled={saving} style={{
          padding: '11px 26px', borderRadius: '8px', border: 'none', cursor: saving ? 'default' : 'pointer',
          background: c.primary, color: c.background, fontWeight: 700, fontSize: '0.95rem', fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
        }}>{saving ? 'Guardando…' : 'Guardar configuración'}</button>
        {msg && <span style={{ color: msg.ok ? c.success : c.error, fontSize: '0.88rem' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

export default CajeroConfig;
